import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
    const [playerState, setPlayerState] = useState({
        attractionId: null,
        attractionTitle: '',
        tracks: [],
        currentTrack: 0,
        playing: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        muted: false,
        audioSrc: null
    });
    const audioRef = useRef(null);
    const srcResolverRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaSourceRef = useRef(null);

    // Core function: ensure AudioContext is created, resumed, and connected.
    // Must be called during a user gesture for first-time creation.
    // Safe to call multiple times — idempotent.
    const ensureAudioContext = useCallback(async () => {
        try {
            if (!audioContextRef.current) {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                if (AudioCtx) {
                    audioContextRef.current = new AudioCtx();
                }
            }

            const ctx = audioContextRef.current;
            if (!ctx) return false;

            // Resume if suspended (required on iOS/Android after creation)
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }

            // Connect <audio> to AudioContext destination (once only).
            // This routes all audio through WebAudio which follows OS output
            // routing (Bluetooth, wired headphones, etc.)
            const audio = audioRef.current;
            if (audio && !mediaSourceRef.current) {
                const source = ctx.createMediaElementSource(audio);
                source.connect(ctx.destination);
                mediaSourceRef.current = source;
            }

            return true;
        } catch (e) {
            console.warn('AudioContext setup:', e);
            return false;
        }
    }, []);

    // Early unlock: set up AudioContext on first user gesture (touch/click).
    // This pre-arms the audio pipeline so that future play() calls route
    // through AudioContext immediately.
    useEffect(() => {
        let unlocked = false;

        const handleGesture = async () => {
            if (unlocked) return;
            const ok = await ensureAudioContext();
            if (ok) {
                unlocked = true;
                document.removeEventListener('touchstart', handleGesture, true);
                document.removeEventListener('click', handleGesture, true);
            }
            // On failure: keep listeners so it retries on next gesture
        };

        document.addEventListener('touchstart', handleGesture, true);
        document.addEventListener('click', handleGesture, true);

        return () => {
            document.removeEventListener('touchstart', handleGesture, true);
            document.removeEventListener('click', handleGesture, true);
        };
    }, [ensureAudioContext]);

    const getFileUrl = useCallback((filePath) => {
        if (!filePath) return null;
        return `${API}/api/files/${filePath}`;
    }, []);

    const resolveTrackSrc = useCallback(async (track) => {
        if (!track) return null;
        if (srcResolverRef.current) {
            try {
                const src = await srcResolverRef.current(track);
                if (src) return src;
            } catch (e) {
                console.error('Source resolver error:', e);
            }
        }
        return getFileUrl(track.file_path);
    }, [getFileUrl]);

    // Auto-resolve audio source when current track changes
    const trackKey = `${playerState.attractionId}_${playerState.currentTrack}_${playerState.tracks.length}`;
    useEffect(() => {
        let cancelled = false;
        const track = playerState.tracks[playerState.currentTrack];
        if (track && playerState.attractionId) {
            resolveTrackSrc(track).then(src => {
                if (!cancelled && src) {
                    setPlayerState(prev => ({ ...prev, audioSrc: src }));
                }
            });
        }
        return () => { cancelled = true; };
    }, [trackKey, resolveTrackSrc]);

    const playTracks = useCallback((tracks, startIndex, attractionId, attractionTitle, resolver) => {
        // Ensure AudioContext on this user gesture
        ensureAudioContext();
        srcResolverRef.current = resolver || null;
        setPlayerState(prev => ({
            ...prev,
            attractionId,
            attractionTitle,
            tracks,
            currentTrack: startIndex,
            playing: true,
            currentTime: 0,
            duration: 0,
            audioSrc: null
        }));
    }, [ensureAudioContext]);

    const togglePlay = useCallback(() => {
        // Ensure AudioContext on this user gesture (play button tap)
        ensureAudioContext();
        setPlayerState(prev => ({ ...prev, playing: !prev.playing }));
    }, [ensureAudioContext]);

    const pause = useCallback(() => {
        setPlayerState(prev => ({ ...prev, playing: false }));
    }, []);

    const nextTrack = useCallback(() => {
        setPlayerState(prev => {
            if (prev.currentTrack < prev.tracks.length - 1) {
                return { ...prev, currentTrack: prev.currentTrack + 1, currentTime: 0, audioSrc: null };
            }
            return { ...prev, playing: false };
        });
    }, []);

    const prevTrack = useCallback(() => {
        setPlayerState(prev => {
            if (prev.currentTrack > 0) {
                return { ...prev, currentTrack: prev.currentTrack - 1, currentTime: 0, audioSrc: null };
            }
            return prev;
        });
    }, []);

    const seek = useCallback((time) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
        }
        setPlayerState(prev => ({ ...prev, currentTime: time }));
    }, []);

    const setVolume = useCallback((vol) => {
        setPlayerState(prev => ({ ...prev, volume: vol, muted: vol === 0 }));
    }, []);

    const toggleMute = useCallback(() => {
        setPlayerState(prev => ({ ...prev, muted: !prev.muted }));
    }, []);

    const dismiss = useCallback(() => {
        srcResolverRef.current = null;
        // Update MediaSession
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'none';
        }
        setPlayerState(prev => ({
            ...prev,
            playing: false,
            attractionId: null,
            attractionTitle: '',
            tracks: [],
            audioSrc: null,
            currentTime: 0,
            duration: 0
        }));
    }, []);

    // Sync play/pause with audio element — also re-arms AudioContext
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (playerState.audioSrc && playerState.playing) {
            // Re-ensure AudioContext is running (handles OS suspension after bg)
            const ctx = audioContextRef.current;
            if (ctx && ctx.state === 'suspended') {
                ctx.resume().catch(() => {});
            }
            audio.play().catch(() => {});
        } else {
            audio.pause();
        }
    }, [playerState.playing, playerState.audioSrc]);

    // Sync volume
    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.volume = playerState.muted ? 0 : playerState.volume;
        }
    }, [playerState.volume, playerState.muted]);

    // MediaSession API — lets the OS know we're playing audio.
    // This improves Bluetooth routing and enables lock-screen controls.
    useEffect(() => {
        if (!('mediaSession' in navigator)) return;

        const track = playerState.tracks[playerState.currentTrack];
        if (!track || !playerState.attractionId) return;

        navigator.mediaSession.metadata = new MediaMetadata({
            title: track.title || 'Audio Guide',
            artist: playerState.attractionTitle || 'Voxoora',
            album: 'Voxoora Audio Guide'
        });

        navigator.mediaSession.playbackState = playerState.playing ? 'playing' : 'paused';

        navigator.mediaSession.setActionHandler('play', () => {
            ensureAudioContext();
            setPlayerState(prev => ({ ...prev, playing: true }));
        });
        navigator.mediaSession.setActionHandler('pause', () => {
            setPlayerState(prev => ({ ...prev, playing: false }));
        });
        navigator.mediaSession.setActionHandler('previoustrack', () => {
            setPlayerState(prev => {
                if (prev.currentTrack > 0) {
                    return { ...prev, currentTrack: prev.currentTrack - 1, currentTime: 0, audioSrc: null };
                }
                return prev;
            });
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => {
            setPlayerState(prev => {
                if (prev.currentTrack < prev.tracks.length - 1) {
                    return { ...prev, currentTrack: prev.currentTrack + 1, currentTime: 0, audioSrc: null };
                }
                return { ...prev, playing: false };
            });
        });
    }, [playerState.attractionId, playerState.currentTrack, playerState.tracks, playerState.attractionTitle, playerState.playing, ensureAudioContext]);

    // Handle audio device changes (Bluetooth connect/disconnect while app is open)
    useEffect(() => {
        if (!navigator.mediaDevices) return;

        const handleDeviceChange = async () => {
            const ctx = audioContextRef.current;
            if (ctx && ctx.state === 'suspended') {
                await ctx.resume().catch(() => {});
            }
        };

        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
        };
    }, []);

    const currentTrackData = playerState.tracks[playerState.currentTrack] || null;

    return (
        <PlayerContext.Provider value={{
            ...playerState,
            currentTrackData,
            audioRef,
            playTracks,
            togglePlay,
            pause,
            nextTrack,
            prevTrack,
            seek,
            setVolume,
            toggleMute,
            dismiss,
            isActive: !!playerState.attractionId
        }}>
            <audio
                ref={audioRef}
                src={playerState.audioSrc}
                crossOrigin="anonymous"
                playsInline
                onTimeUpdate={() => {
                    if (audioRef.current) {
                        setPlayerState(prev => ({ ...prev, currentTime: audioRef.current.currentTime }));
                    }
                }}
                onLoadedMetadata={() => {
                    if (audioRef.current) {
                        setPlayerState(prev => ({ ...prev, duration: audioRef.current.duration }));
                    }
                }}
                onEnded={nextTrack}
                onError={() => {
                    setPlayerState(prev => ({ ...prev, playing: false }));
                }}
            />
            {children}
        </PlayerContext.Provider>
    );
}

export const usePlayer = () => {
    const ctx = useContext(PlayerContext);
    if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
    return ctx;
};
